export default function TwelveMonthRoadmap() {
  const quarters = [
    {
      quarter: 'Q1',
      title: 'Foundation & Setup',
      bgColor: 'bg-[#4A7BFF]',
      lightColor: 'bg-blue-50',
      months: [
        {
          month: 'Month 1',
          activities: [
            'Infrastructure assessment and planning',
            'Lab construction and equipment installation',
            'Safety audit and compliance check'
          ]
        },
        {
          month: 'Month 2',
          activities: [
            'Teacher recruitment and initial training',
            'Curriculum mapping and customization',
            'LMS setup and configuration'
          ]
        },
        {
          month: 'Month 3',
          activities: [
            'Pilot classes with selected groups',
            'Equipment testing and calibration',
            'Parent orientation sessions'
          ]
        }
      ],
      deliverables: [
        'Fully functional STEM lab',
        'Trained teaching staff',
        'Customized curriculum',
        'Safety certification'
      ],
      metric: null,
      metricLabel: null
    },
    {
      quarter: 'Q2',
      title: 'Launch & Engagement',
      bgColor: 'bg-[#00C853]',
      lightColor: 'bg-green-50',
      months: [
        {
          month: 'Month 4',
          activities: [
            'Full-scale student onboarding',
            'Regular classes commence',
            'First parent-teacher meetings'
          ]
        },
        {
          month: 'Month 5',
          activities: [
            'Student project initiation',
            'Mid-term assessments',
            'Teacher feedback sessions'
          ]
        },
        {
          month: 'Month 6',
          activities: [
            'First innovation workshop',
            'Parent engagement programs',
            'Quarterly performance review'
          ]
        }
      ],
      deliverables: [
        'All students enrolled',
        'First projects completed',
        'Parent satisfaction survey',
        'Q1 performance report'
      ],
      metric: '85%',
      metricLabel: 'Learning Outcomes'
    },
    {
      quarter: 'Q3',
      title: 'Acceleration & Excellence',
      bgColor: 'bg-[#9C27FF]',
      lightColor: 'bg-purple-50',
      months: [
        {
          month: 'Month 7',
          activities: [
            'Advanced module introduction',
            'Competition preparation begins',
            'Career guidance sessions start'
          ]
        },
        {
          month: 'Month 8',
          activities: [
            'Inter-school competitions',
            'Industry expert guest lectures',
            'Advanced project work'
          ]
        },
        {
          month: 'Month 9',
          activities: [
            'Comprehensive assessments',
            'Portfolio building workshops',
            'Certification programs'
          ]
        }
      ],
      deliverables: [
        'Competition participation',
        'Advanced projects',
        'Student portfolios',
        'Mid-year report'
      ],
      metric: '90%',
      metricLabel: 'Learning Outcomes'
    },
    {
      quarter: 'Q4',
      title: 'Showcase & Renewal',
      bgColor: 'bg-[#FF6B00]',
      lightColor: 'bg-orange-50',
      months: [
        {
          month: 'Month 10',
          activities: [
            'Annual exhibition planning',
            'Final project presentations',
            'Parent showcase events'
          ]
        },
        {
          month: 'Month 11',
          activities: [
            'Grand STEM exhibition',
            'Award ceremonies',
            'Media coverage and PR'
          ]
        },
        {
          month: 'Month 12',
          activities: [
            'Annual performance review',
            'Renewal planning and upgrades',
            'Next year curriculum planning'
          ]
        }
      ],
      deliverables: [
        'Annual exhibition',
        'Student certifications',
        'Comprehensive report',
        'Renewal proposal'
      ],
      metric: '95%',
      metricLabel: 'Parent Satisfaction'
    }
  ];

  return (
    <section id="integration" className="py-24 bg-gradient-to-br from-white via-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            Structured Academic Integration Timeline
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            12-Month Partnership Model
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Structured implementation with measurable milestones at every quarter
          </p>
        </div>

        {/* Timeline */}
        <div className="space-y-12">
          {quarters.map((quarter, idx) => (
            <div
              key={idx}
              className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
            >
              {/* Quarter Header - Full width colored banner */}
              <div className={`${quarter.bgColor} p-6 relative`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{quarter.title}</h3>
                  </div>
                  {/* Quarter number badge in top-right */}
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 text-center">
                    <div className="text-xl font-bold text-white">{quarter.quarter}</div>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Monthly Timeline - Horizontal cards with flow arrows */}
                <div className="flex flex-col lg:flex-row gap-4 mb-8">
                  {quarter.months.map((monthData, mIdx) => (
                    <div key={mIdx} className="flex items-center flex-1">
                      {/* Month Card */}
                      <div className="bg-white rounded-xl p-5 border border-gray-200 flex-1 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                          {/* Circular numbered badge */}
                          <div className={`w-8 h-8 rounded-full ${quarter.bgColor} flex items-center justify-center text-white font-bold text-sm`}>
                            {mIdx + 1}
                          </div>
                          <h4 className="text-base font-bold text-gray-900">{monthData.month}</h4>
                        </div>
                        <ul className="space-y-2">
                          {monthData.activities.map((activity, aIdx) => (
                            <li key={aIdx} className="flex items-start gap-2 text-sm text-gray-700">
                              <i className="ri-checkbox-circle-fill text-green-500 flex-shrink-0 mt-0.5"></i>
                              <span>{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {/* Flow arrow between months */}
                      {mIdx < quarter.months.length - 1 && (
                        <div className="hidden lg:flex items-center justify-center px-3">
                          <i className="ri-arrow-right-line text-2xl text-gray-400"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Key Deliverables - Light blue/gray background with gift box icon */}
                <div className="bg-slate-100 rounded-2xl p-6 mb-6">
                  <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-gift-line text-lg text-blue-600"></i>
                    Key Deliverables
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {quarter.deliverables.map((deliverable, dIdx) => (
                      <div key={dIdx} className="bg-white rounded-lg p-4 text-center shadow-sm">
                        <i className="ri-checkbox-circle-fill text-2xl text-green-500 mb-2"></i>
                        <p className="text-sm font-medium text-gray-900">{deliverable}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Success Metric - Light green background bar with chart icon */}
                {quarter.metric && (
                  <div className="flex items-center justify-center gap-4 bg-green-100 rounded-xl p-4">
                    <i className="ri-bar-chart-line text-3xl text-green-600"></i>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-gray-900">{quarter.metric}</div>
                      <div className="text-sm text-gray-600">{quarter.metricLabel}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-7 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line text-base"></i>
            Download Complete Implementation Guide
          </a>
        </div>
      </div>
    </section>
  );
}